export interface Matter {
  id: string;
  title: string;
  jurisdiction: 'EP' | 'US' | 'IN';
  current_stage: 'stage_0' | 'stage_1' | 'stage_2' | 'stage_2_5' | 'stage_3' | 'stage_4';
  created_at: string;
  updated_at: string;
  // Matter-management metadata — all optional, set server-side.
  docket_id?: string | null;
  client_name?: string | null;
  client_reference?: string | null;
  invention_title?: string | null;
  technology_area?: string | null;
  filing_deadline?: string | null;
  priority_date?: string | null;
  tags?: string | null;
  archived?: boolean | number | null;
  archived_at?: string | null;
  status?: string | null;
  claims_frozen_at?: string | null;
  // ACL — admin sees every matter in workspace, drafter sees own +
  // explicitly granted. Surface owner_id so the Sharing modal can
  // distinguish owner from collaborator and the UI can render an
  // owner badge.
  owner_id?: number | null;
  workspace_id?: number | null;
}

export interface IdfDocument {
  id: string;
  matter_id: string;
  filename: string;
  mime_type: string;
  extracted_text: string;
  extraction_warnings: string[];
  uploaded_at: string;
}

export interface StageState {
  id: string;
  matter_id: string;
  stage: string;
  content: any;
  is_frozen: boolean | number;
  frozen_at: string | null;
}

export interface Claim {
  id: string;
  matter_id: string;
  claim_number: number;
  claim_type: 'independent' | 'dependent';
  parent_claim_number: number | null;
  category: 'method' | 'system' | 'crm' | 'apparatus' | 'other';
  claim_text: string;
  analysis: ClaimAnalysis;
  version_history: VersionEntry[];
  is_frozen: boolean;
  placeholder_count: number;
}

export interface ClaimAnalysis {
  distinguishing_features?: string;
  key_terms?: string[];
  technical_effect?: string;
  advantage?: string;
}

export interface VersionEntry {
  claim_text: string;
  analysis: ClaimAnalysis;
  generated_at: string;
}

export interface QaRound {
  id: string;
  matter_id: string;
  round_number: number;
  questions: QaQuestion[];
  score_after: Scores;
  created_at: string;
}

export interface QaQuestion {
  parameter: string;
  /** The drafter's score for the parameter when the question was generated,
   *  used for the gamified "65% → 100%" banner. */
  current_score?: number;
  /** One-sentence hint from the LLM describing the concrete detail that,
   *  when supplied, would close the remaining gap on this parameter. */
  target_improvement?: string;
  question: string;
  /** Indicative answer hints — the drafter may still answer with free-text
   *  technical detail instead of picking one. */
  options: string[];
  selected_answer?: string;
  free_text?: string;
}

export interface Scores {
  problem: number;
  solution: number;
  technical_effect: number;
  prior_art: number;
  embodiments: number;
}

export interface QcFinding {
  category: string;
  severity: 'info' | 'warning' | 'critical';
  claim_number: number;
  message: string;
  suggested_fix: string;
}

export interface PromptConfig {
  id: string;
  name: string;
  stage: string;
  tier: string;
  template: string;
  variables: string[];
}

export interface MatterFullState {
  matter: Matter;
  stages: Record<string, StageState>;
  claims: Claim[];
  idf_documents: IdfDocument[];
  qa_rounds: QaRound[];
}

export type Stage = 'stage_0' | 'stage_1' | 'stage_2' | 'stage_2_5' | 'stage_3' | 'stage_4';

export const STAGE_LABELS: Record<Stage, string> = {
  stage_0: 'Upload',
  stage_1: 'IDF Understanding',
  stage_2: 'Novelty & Inventive Step',
  stage_2_5: 'Claim Strategy',
  stage_3: 'Claim Drafting',
  stage_4: 'QC & Export',
};

// stage_0 sits at the front — new matters land here for document
// upload before Stage 1 analysis. stage_2_5 is still in the array
// for routing, but TopBar filters it out of the visible progress
// bar (merged into Stage 2 as the Strategy tab).
export const STAGE_ORDER: Stage[] = ['stage_0', 'stage_1', 'stage_2', 'stage_2_5', 'stage_3', 'stage_4'];
